import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy,
  QrCode2,
  WhatsApp as WhatsAppIcon,
  OpenInNew,
  Download,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { buildWhatsAppHref } from '../common/WhatsAppLink';

interface PublicSalonInfo {
  salonId: string;
  name: string;
  phone: string;
}

const BOOKING_PATH = '/menu';

const BookingLinkCard: React.FC = () => {
  const [salon, setSalon] = useState<PublicSalonInfo | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ data?: PublicSalonInfo }>('/salon/public');
        if (!cancelled && res?.data) setSalon(res.data);
      } catch {
        // Best-effort — without salon info the card still shows the URL/QR.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bookingUrl = `${window.location.origin}${BOOKING_PATH}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(
    bookingUrl
  )}`;
  const qrDownloadHref = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=20&data=${encodeURIComponent(
    bookingUrl
  )}`;

  const whatsAppGreeting = salon?.phone
    ? buildWhatsAppHref(
        salon.phone,
        `Hi ${salon.name}, I'd like to book an appointment. ${bookingUrl}`
      )
    : '';

  const handleCopy = async () => {
    try {
      await window.navigator.clipboard.writeText(bookingUrl);
      setSnack('Booking link copied');
    } catch {
      setSnack('Copy failed — select and copy manually');
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        background: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid rgba(232, 180, 184, 0.25)',
        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.06)',
        mb: 3,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          {/* QR */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#FAFAFA',
              border: '1px solid rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={qrSrc}
              alt="Booking link QR"
              sx={{ width: 180, height: 180, display: 'block' }}
            />
            <Button
              size="small"
              startIcon={<Download />}
              component="a"
              href={qrDownloadHref}
              download="booking-qr.png"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#5C2A30', textTransform: 'none' }}
            >
              Download QR
            </Button>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <QrCode2 sx={{ color: '#5C2A30' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#5C2A30' }}>
                Share your booking link
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste this link in your WhatsApp Business App profile, automated
              greeting, and catalog. Print the QR for the front desk.
            </Typography>

            <TextField
              fullWidth
              size="small"
              value={bookingUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copy link" arrow>
                    <IconButton size="small" onClick={() => void handleCopy()}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<OpenInNew />}
                component="a"
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: '#E8B4B8',
                  color: '#5C2A30',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Open booking page
              </Button>
              {whatsAppGreeting && (
                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  component="a"
                  href={whatsAppGreeting}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(37,211,102,0.35)',
                    '&:hover': { bgcolor: '#1FB957' },
                  }}
                >
                  Send via WhatsApp
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};

export default BookingLinkCard;
